import React from 'react';
import { get } from 'lodash';
import { FaCheck } from 'react-icons/fa';
import { FiCopy } from 'react-icons/fi';
import { Collection } from 'core/model';
import { ENTITY_TYPES } from '@kentik/ui-shared/hybrid/constants';
import CopyToClipboardButton from 'app/components/CopyToClipboardButton';
import { Flex, Card, Text, Tag, Button, Heading } from 'core/components';
import { VirtualizedTable } from 'core/components/table';
import CloudIcon from 'app/views/hybrid/maps/components/CloudIcon';

import AwsFirewallRuleGroupTrafficCharts from './FirewallRuleGroupTrafficCharts';

const { FIREWALL_POLICY, FIREWALL_RULE_GROUP } = ENTITY_TYPES.get('aws');

const AwsFirewallRuleGroups = (props) => {
  const { id, topology, firewallId } = props;

  // id is a firewall policy ID
  if (!id) {
    return null;
  }

  const firewallPolicyEntity = get(topology.Entities, `${[FIREWALL_POLICY]}.${id}`);
  const { FirewallPolicy } = firewallPolicyEntity;

  const { StatefulRuleGroupReferences = [] } = FirewallPolicy;

  if (StatefulRuleGroupReferences.length === 0) {
    return <Text>No firewall rulte groups specified.</Text>;
  }

  const firewallRuleGroups = StatefulRuleGroupReferences.map((ruleGroupReference) => {
    const { ResourceArn, Priority } = ruleGroupReference;

    const firewallRuleGroupEntity = get(topology.Entities, `${[FIREWALL_RULE_GROUP]}.${ResourceArn}`);

    return (firewallRuleGroupEntity?.RuleGroup?.RulesSource?.StatefulRules ?? []).map((firewallRuleGroup) => {
      const { Action, Header } = firewallRuleGroup;

      return {
        ...Header,
        action: Action,
        priority: Priority,
        firewallRuleGroup: firewallRuleGroupEntity
      };
    });
  }).flat();

  const firewallRuleGroupCollection = new Collection(firewallRuleGroups, {
    groupBy: 'firewallRuleGroup.Name'
  });

  const columns = [
    {
      label: 'Action',
      name: 'action',
      width: 50
    },
    {
      label: 'Protocol',
      name: 'Protocol'
    },
    {
      label: 'Source',
      name: 'Source',
      width: 100
    },
    {
      label: 'SourcePort',
      name: 'SourcePort'
    },
    {
      label: 'Direction',
      name: 'Direction'
    },
    {
      label: 'Destination',
      name: 'Destination',
      width: 100
    },
    {
      label: 'DestinationPort',
      name: 'DestinationPort'
    }
  ];

  const groupSummary = ({ group }) => {
    const { totalPassRules, totalDropRules, totalAlertRules } = group.reduce(
      (acc, model) => {
        const action = model.get('action');

        if (action === 'PASS') {
          acc.totalPassRules += 1;
        }

        if (action === 'ALERT') {
          acc.totalAlertRules += 1;
        }

        if (action === 'DROP') {
          acc.totalDropRules += 1;
        }

        return acc;
      },
      {
        totalPassRules: 0,
        totalDropRules: 0,
        totalAlertRules: 0
      }
    );

    return (
      <Flex alignItems="center">
        {totalPassRules > 0 && (
          <Tag mr={1} intent="success" icon={FaCheck} title={`${totalPassRules} PASS`} minimal round>
            {totalPassRules}
          </Tag>
        )}

        {totalAlertRules > 0 && (
          <Tag mr={1} intent="warning" icon="error" title={`${totalAlertRules} ALERT`} minimal round>
            {totalAlertRules}
          </Tag>
        )}

        {totalDropRules > 0 && (
          <Tag mr={1} intent="danger" icon="error" title={`${totalDropRules} DROP`} minimal round>
            {totalDropRules}
          </Tag>
        )}

        <Flex alignItems="center">
          <Text fontWeight="bold" mr={1} ellipsis>
            Firewall Rule Group: {group[0].get('firewallRuleGroup.Name')}
          </Text>
          <CopyToClipboardButton
            text={group[0].get('firewallRuleGroup.id')}
            copyConfirmationText={null}
            intent="none"
            minimal
            small
          >
            <Button icon={FiCopy} />
          </CopyToClipboardButton>
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex flexDirection="column" flex={1} gap={2} mb={5}>
      <Heading level={4} alignSelf="flex-start">
        <CloudIcon
          cloudProvider="aws"
          entity={FIREWALL_RULE_GROUP}
          width={24}
          height={24}
          style={{ marginRight: '8px' }}
        />
        Firewall Rule Groups
      </Heading>
      <Card height="100%">
        <VirtualizedTable
          style={{ height: 300 }}
          collection={firewallRuleGroupCollection}
          groupSummaryLookup={groupSummary}
          columns={columns}
          selectOnRowClick={false}
          isCollapsed
          flexed
        />
      </Card>

      <AwsFirewallRuleGroupTrafficCharts firewallId={firewallId} />
    </Flex>
  );
};

export default AwsFirewallRuleGroups;
